/**
 * Guided "Make a presentation" flow: ask for the topic, then the number of
 * slides, then hand one complete request to the presentation copilot.
 * Nothing is generated until both answers are known.
 */

export const MAX_PRESENTATION_SLIDES = 30; // mirrors MAX_SLIDES in generatePresentation.js

const NUMBER_WORDS = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17,
  eighteen: 18, nineteen: 19, twenty: 20, thirty: 30,
};

const SLIDE_COUNT_CHIPS = [5, 8, 10, 15];

/** "Make a presentation" / "create presentation" with no topic. */
export function isBarePresentationAsk(text) {
  const t = String(text || "").trim().toLowerCase().replace(/[.!?]+$/, "");
  return (
    /^(please\s+)?(make|create|creat|build|start|new)\s+(a\s+|an\s+|new\s+)*[a-z]*pres[a-z]*t[a-z]*ions?$/.test(t) ||
    t === "presentation" ||
    t === "a presentation"
  );
}

/** Slide count inside a sentence ("… with 8 slides"). */
export function slideCountInText(text) {
  const t = String(text || "").toLowerCase();
  const digit = t.match(/\b(\d{1,3})\s*(slides?|pages?)\b/);
  if (digit) return Number(digit[1]);
  const word = t.match(/\b([a-z]+)\s+(slides?|pages?)\b/);
  return word && NUMBER_WORDS[word[1]] ? NUMBER_WORDS[word[1]] : null;
}

/** Answer to "how many slides?" — accepts "8", "8 slides", "eight". */
export function parseSlideCountAnswer(text) {
  const t = String(text || "").trim().toLowerCase();
  const inSentence = slideCountInText(t);
  if (inSentence) return inSentence;
  const digit = t.match(/^(\d{1,3})\b/);
  if (digit) return Number(digit[1]);
  // A number word only counts when it is the whole answer ("eight", "eight please").
  const word = t.match(/^([a-z]+)(\s+(slides?|please))?[.!]?$/);
  return word && NUMBER_WORDS[word[1]] ? NUMBER_WORDS[word[1]] : null;
}

function cleanTopic(text) {
  return String(text || "")
    .trim()
    .replace(/^(make|create|build)\s+(a\s+)?presentation\s+(about|on|for)\s+/i, "")
    .replace(/^(about|on|for|topic:?)\s+/i, "")
    .replace(/[.!?]+$/, "")
    .trim();
}

function countChips() {
  return [
    ...SLIDE_COUNT_CHIPS.map((n) => ({
      id: `presentation_setup_count:${n}`,
      label: `${n} slides`,
      prompt: `${n} slides`,
    })),
    { id: "presentation_setup_count:auto", label: "You decide", prompt: "You decide" },
  ];
}

const QUESTION = /^(what|how|why|who|when|where|which|can|could|do|does|is|are|should|would)\b.*\??$|\?$/i;
// "for first-year students" is guidance, so students only count as another task when listed/shown.
const OTHER_TASK =
  /\b(quiz|quizzes|results?|marks?|grades?|question bank|cancel|stop|never ?mind|forget it)\b|\b(list|show|see)\b.*\bstudents?\b/i;
const LET_AI_DECIDE = /\b(you decide|up to you|your (choice|call)|any(thing)?|whatever|default|not sure|don'?t know|no idea|doesn'?t matter|as (many|you) (as )?(needed|think|like|see fit))\b/i;
const RECOMMEND = /\b(recommend|suggest|advise|how many should|ideal|best)\b/i;
const TOPIC_CHANGE =
  /^(?:(?:actually|instead|no|rather)[,.]?\s+)?(?:change (?:the )?topic(?: to)?|make it (?:about|on)|switch (?:it )?to|let'?s do)\s+(?:about\s+|on\s+)?(.+)$|^(?:actually|instead|rather)[,.]?\s+(?:about\s+|on\s+)?(.+)$/i;

/** What the professor said besides the count ("10, for first-years" → "for first-years"). */
function extraDetail(text) {
  return String(text || "")
    .replace(/\b(with\s+)?(\d{1,3}|[a-z]+)\s*(slides?|pages?)\b/gi, " ")
    .replace(/^\s*\d{1,3}\b/, " ")
    .replace(new RegExp(`^\\s*(${Object.keys(NUMBER_WORDS).join("|")})\\b(\\s+please)?`, "i"), " ")
    .replace(/^[\s,.;:-]+|[\s,.;:-]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function withNotes(request, notes) {
  const list = (notes || []).filter(Boolean);
  return list.length ? `${request}\nInstructions: ${list.join(". ")}` : request;
}

function recommendedCount(topic) {
  return /\b(overview|introduction|basics?|summary)\b/i.test(topic) ? 5 : 8;
}

/**
 * Advance the setup. `setup` is null (idle), { step: "topic" } or
 * { step: "count", topic, notes, nudged }. Returns:
 *  - { setup, reply, options }    → answer locally and wait for the user
 *  - { setup: null, request }     → send `request` to the presentation copilot
 *  - { setup, passThrough: true } → let the assistant answer this normally
 *  - null                         → not part of this flow
 */
export function advancePresentationSetup(setup, text) {
  const raw = String(text || "").trim();
  if (!raw) return null;

  if (isBarePresentationAsk(raw)) {
    return {
      setup: { step: "topic" },
      reply: "Sure — what topic should the presentation cover?",
      options: [],
    };
  }

  if (!setup) return null;
  if (OTHER_TASK.test(raw)) return { setup: null, passThrough: true };

  if (setup.step === "topic") {
    // A question is answered normally; the topic question stays open.
    if (QUESTION.test(raw)) return { setup, passThrough: true };
    const topic = cleanTopic(raw);
    if (topic.length < 2) {
      return { setup, reply: "What topic should the presentation cover?", options: [] };
    }
    const count = slideCountInText(raw);
    if (count && count <= MAX_PRESENTATION_SLIDES) {
      return { setup: null, request: buildRequest(cleanTopic(extraDetail(raw)) || topic, count) };
    }
    return {
      setup: { step: "count", topic, notes: [] },
      reply: `How many slides should “${topic}” have? (1–${MAX_PRESENTATION_SLIDES})`,
      options: countChips(),
    };
  }

  if (setup.step === "count") {
    const notes = setup.notes || [];
    const count = parseSlideCountAnswer(raw);

    if (count) {
      if (count < 1 || count > MAX_PRESENTATION_SLIDES) {
        return {
          setup,
          reply: `One draft can have 1 to ${MAX_PRESENTATION_SLIDES} slides. How many would you like?`,
          options: countChips(),
        };
      }
      return {
        setup: null,
        request: withNotes(buildRequest(setup.topic, count), [...notes, extraDetail(raw)]),
      };
    }

    if (LET_AI_DECIDE.test(raw)) {
      return {
        setup: null,
        request: withNotes(
          buildRequest(setup.topic, recommendedCount(setup.topic)),
          [...notes, extraDetail(raw.replace(LET_AI_DECIDE, ""))]
        ),
      };
    }

    if (RECOMMEND.test(raw)) {
      const n = recommendedCount(setup.topic);
      return {
        setup,
        reply: `For “${setup.topic}” I’d suggest about ${n} slides — enough for an introduction, the key structures, and a summary. Use ${n}, or pick another number.`,
        options: [
          { id: `presentation_setup_count:${n}`, label: `Use ${n} slides`, prompt: `${n} slides` },
          ...countChips().filter((c) => c.prompt !== `${n} slides`),
        ],
      };
    }

    const topicChange = raw.match(TOPIC_CHANGE);
    if (topicChange && (topicChange[1] || topicChange[2])) {
      const topic = cleanTopic(topicChange[1] || topicChange[2]);
      const inline = slideCountInText(raw);
      if (inline && inline <= MAX_PRESENTATION_SLIDES) {
        return { setup: null, request: withNotes(buildRequest(cleanTopic(extraDetail(topic)) || topic, inline), notes) };
      }
      return {
        setup: { step: "count", topic, notes },
        reply: `Okay, “${topic}”. How many slides should it have?`,
        options: countChips(),
      };
    }

    // A question unrelated to the size: answer it, keep waiting for the count.
    if (QUESTION.test(raw)) return { setup, passThrough: true };

    // Anything else is guidance for the deck (audience, focus, style...). Keep it.
    const nextNotes = [...notes, raw];
    if (setup.nudged) {
      // Second answer without a number: go ahead instead of asking again.
      return {
        setup: null,
        request: withNotes(buildRequest(setup.topic, recommendedCount(setup.topic)), nextNotes),
      };
    }
    return {
      setup: { step: "count", topic: setup.topic, notes: nextNotes, nudged: true },
      reply: `Got it — I’ll take that into account. How many slides should “${setup.topic}” have? Or tap “You decide”.`,
      options: countChips(),
    };
  }
  return null;
}

export function buildRequest(topic, count) {
  return `Make a presentation about ${topic || "this topic"} with ${count} slides`;
}
